import { AudioLines, Check, CircleAlert, CloudOff, LoaderCircle } from 'lucide-react';
import { defineMessages } from 'react-intl';

import type {
  SyncBadgeStatus,
  SyncStatusBadgeConfig,
  SyncStatusTone,
} from './sync-status-badge.types';

/**
 * Tone → dark-mode-safe text token. The single source of truth for the sync
 * status colour, shared by every consumer (the badge, and the session-list
 * `offline-sync-indicator`) so they can't drift. The lucide glyph and any label
 * inherit it via `currentColor`.
 */
export const syncStatusToneTextClass: Record<SyncStatusTone, string> = {
  success: 'text-validation-success',
  destructive: 'text-validation-destructive',
  neutral: 'text-text-tertiary',
};

/**
 * Locked design treatment for each of the 9 offline sync states, matching Marc's
 * Figma "SessionHeaderStatus" / "SessionListItem" (the literal design — bare
 * lucide glyphs tinted by tone, neutral in-flight, tooltips for icon-only states
 * — not the earlier Chip + StatusIndicator inline-label recipe).
 *
 * Tone → colour: `recording`/`synced` are green (`success`); the in-flight and
 * `pending_sync` states are `neutral`; all failures are red (`destructive`).
 *
 * Presentation: only the in-flight ("Syncing…") and `synced` ("Synced") states
 * show a visible label; `pending_sync`, every failure, and `recording` are
 * icon-only with a tooltip.
 *
 * Glyph: concrete `lucide-react` icons for every state, tinted by tone —
 * `synced` is a green `Check` (the DS `StatusIndicator status="done"` is
 * colour-locked to yellow, so it can't render Marc's green check), and the
 * failures use `CircleAlert` (a circle-!). Both `Check` and `CircleAlert` sit
 * outside the DS `StatusIndicator` vocabulary (which offers only a yellow check
 * and a triangle alert) — a deliberate bend of the "status visuals go through
 * StatusIndicator" rule to match Marc; flagged for the design-system team.
 *
 * The three in-flight states (`creating_session`, `uploading_audio`,
 * `processing`) deliberately collapse to one spinning `LoaderCircle` + "Syncing…" —
 * the user sees one calm "we're working on it" affordance, not pipeline
 * internals.
 *
 * `note_failed` is here but is not a `sync_tracking` state (see
 * `SyncBadgeStatus`): consumers resolve it from durable client state rather than
 * the queue, because the queue row is already gone by the time it matters. It
 * takes the same destructive treatment as the sync failures, since to the
 * clinician it reads as one more thing that didn't work.
 *
 * `sync_interrupted` stays absent: it is a Chronicle transfer state with no
 * analogue in HTTP desktop sync, and nothing in this app emits it.
 *
 * Both surfaces additionally suppress `recording` (the recorder owns that
 * affordance); the entry is kept here so the union stays exhaustive and the
 * story can render it.
 */
export const offlineSyncStatusBadgeConfig: Record<
  SyncBadgeStatus,
  SyncStatusBadgeConfig
> = {
  recording: {
    tone: 'success',
    glyph: { icon: AudioLines },
    presentation: 'iconOnly',
  },
  pending_sync: {
    tone: 'neutral',
    glyph: { icon: CloudOff },
    presentation: 'iconOnly',
  },
  creating_session: {
    tone: 'neutral',
    glyph: { icon: LoaderCircle, spin: true },
    presentation: 'inline',
  },
  uploading_audio: {
    tone: 'neutral',
    glyph: { icon: LoaderCircle, spin: true },
    presentation: 'inline',
  },
  processing: {
    tone: 'neutral',
    glyph: { icon: LoaderCircle, spin: true },
    presentation: 'inline',
  },
  synced: {
    tone: 'success',
    glyph: { icon: Check },
    presentation: 'inline',
  },
  sync_failed: {
    tone: 'destructive',
    glyph: { icon: CircleAlert },
    presentation: 'iconOnly',
  },
  upload_failed: {
    tone: 'destructive',
    glyph: { icon: CircleAlert },
    presentation: 'iconOnly',
  },
  auth_required: {
    tone: 'destructive',
    glyph: { icon: CircleAlert },
    presentation: 'iconOnly',
  },
  unrecoverable: {
    tone: 'destructive',
    glyph: { icon: CircleAlert },
    presentation: 'iconOnly',
  },
  note_failed: {
    tone: 'destructive',
    glyph: { icon: CircleAlert },
    presentation: 'iconOnly',
  },
};

/**
 * Localized copy for each sync status. For `inline` states this is the visible
 * label ("Syncing…", "Synced"); for `iconOnly` states it is both the tooltip
 * content and the glyph's `aria-label`. The badge resolves only the active
 * status's message per render (a single `intl.formatMessage(...)`), rather than
 * building the full 9-entry map every render.
 *
 * The three in-flight states share "Syncing…"; the two non-auth failures share
 * the same "we'll keep retrying" copy. Duplicate `defaultMessage`s resolve to
 * the same generated id — that's intended (one translation, several states).
 * The detailed, error-code-specific failure messaging is a separate concern
 * owned by the failure toast / banner, not this pill.
 *
 * No instruction whose affordance is surface-specific. Every surface renders
 * the same string, so an instruction only works if the action it names is
 * reachable from all of them: `auth_required` says "Sign in again" because
 * sign-in always is, whereas delete is offered by the new list's row context
 * menu and nowhere else, which is why `unrecoverable` stops at the fact.
 * Recovery copy that names a surface-specific control belongs next to that
 * control, not in this shared string.
 *
 * **Rewording one of these has a second consumer that cannot import it.** The
 * desktop-native E2E lane matches these strings as accessible names, and it
 * transcribes them by hand: its Jest project does not resolve the `@/` alias,
 * so `e2e/desktop-native/pages/session-list.page.test.ts` carries its own copy
 * and stays green when the wording here moves, leaving the native selectors
 * dead until someone runs that lane. If you change a message below, update
 * that fixture in the same commit.
 */
export const offlineSyncStatusMessages: Record<
  SyncBadgeStatus,
  { defaultMessage: string; id: string }
> = defineMessages({
  recording: { defaultMessage: 'Transcribing…', id: '2gN9tncAmU' },
  pending_sync: {
    defaultMessage: "Saved offline. Your session will sync once you're back online.",
    id: 'hhPc5GbMtm',
  },
  creating_session: { defaultMessage: 'Syncing…', id: 'ZBXWfWmBKK' },
  uploading_audio: { defaultMessage: 'Syncing…', id: 'ZBXWfWmBKK' },
  processing: { defaultMessage: 'Syncing…', id: 'ZBXWfWmBKK' },
  synced: { defaultMessage: 'Synced', id: 'TRfaIL7S/7' },
  sync_failed: {
    defaultMessage: "Couldn't sync your session, we'll keep retrying.",
    id: 'oV4mtvux2m',
  },
  upload_failed: {
    defaultMessage: "Couldn't sync your session, we'll keep retrying.",
    id: 'oV4mtvux2m',
  },
  auth_required: {
    defaultMessage: "Couldn't sync your session. Sign in again to finish.",
    id: 'Gp4cVUsBTZ',
  },
  unrecoverable: {
    defaultMessage: "This session couldn't be recovered",
    id: 'o1GYIfbULD',
  },
  // Deliberately does NOT promise a retry, unlike the sync failures above.
  // Nothing retries a failed note: the engine emits `upload_complete` straight
  // after the failure, the recording becomes `synced`, and the drain only ever
  // revisits non-`synced` recordings. Regenerating is the user's move, and it is
  // reachable from every surface this string renders on.
  note_failed: {
    defaultMessage: "Couldn't generate your note. You can generate it again.",
    id: 'MbfFq5U73E',
  },
});
